import { DLAbbrDesMap } from '../data/driversLicenseAbbrevationDescriptionMap'
import {
  convertGenderString,
  convertHeightString,
  transformDriversLicenseDate,
  convertWeightUnitString,
  convertCountryString,
} from '../../../utilities'
import { getAgeFromReference, getAgeUnitFromReference } from '../../../utilities/dateTime.util'
import { IPatientDemographicsState } from '../../patientDemographics/store/patientDemographics.types'
import { IDriversLicenseScannerState } from '../store/driversLicenseScanner.types'
import { setDriversLicenseKeys, setLicenseInfo } from '../store/driversLicenseScanner.actions'
import { mockLicenseInfo } from '../data/mockLicenseInfo'
import { ToggleKeyConstants } from '../store/driversLicenseScanner.constants'
import { IPatientDemographicsOptionsState } from '../../options/store/options.types'
import {
  convertHeightUnitString,
  convertWeightString,
  convertZIPString,
  getOption,
  isNewYorkStateLicense,
} from '../../../utilities/string.util'
import Logger from '../../../services/logger.service'

export class DriversLicenseScannerService {
  public licenseInformation = {}

  parseDriverLicense(txt: string): void {
    if (!txt || txt.trim() === '') {
      Logger.info('DriversLicenseScanner - Cannot parse null/empty barcodeText')
      return
    }

    const lines = txt.split('\n')
    Logger.info(`DriversLicenseScanner - Number of lines in the barcodeText: ${lines?.length}`)

    const abbrs = Object.keys(DLAbbrDesMap)

    const isNYLicense = isNewYorkStateLicense(lines)
    Logger.info(`DriversLicenseScanner - isNYLicense: ${isNYLicense}`)

    lines.forEach((line: string, idx: number) => {
      let abbr
      let content
      if (idx === 1) {
        abbr = 'DAQ'
        content = line.substring(line.indexOf(abbr) + 3)
      } else {
        abbr = line.substring(0, 3)
        content = line.substring(3).trim()
      }

      if (isNYLicense) {
        if (abbr === 'DCT') {
          const name = line.substring(3).trim().split(',')
          if (name && name.length > 1) {
            // @ts-ignore
            this.licenseInformation['DAD'] = {
              description: DLAbbrDesMap['DAD'],
              content: name[1],
            }
          }
          abbr = 'DAC'
          content = name[0]
        }

        if (abbr === 'DAU') {
          const height = content.split(' ')
          if (height && height.length === 1) {
            const inch = height[0].slice(1) ? parseInt(height[0].slice(1)) : 0
            content = String(parseInt(height[0].slice(0, 1)) * 12 + inch)
          }
        }
      }

      if (abbrs.includes(abbr)) {
        // @ts-ignore
        const description = DLAbbrDesMap[abbr]
        if (description === 'Gender') {
          content = content === '1' ? 'Male' : 'Female'
        }
        // @ts-ignore
        this.licenseInformation[abbr] = {
          description,
          content,
        }
      }
    })

    Logger.info(
      `DriversLicenseScanner - successfully completed full parse. LicenseInformation keys: ${Object.keys(
        this.licenseInformation
      )}`
    )
  }

  resetPatientDemographicsState(pd: IPatientDemographicsState): IPatientDemographicsState {
    pd.firstName = ''
    pd.middleInitial = ''
    pd.lastName = ''
    pd.address1 = ''
    pd.city = null
    pd.state = null
    pd.dlState = null
    pd.zipCode = ''
    pd.driversLicense = ''
    pd.height = null
    pd.heightUnits = null
    pd.weight = null
    pd.weightUnits = null
    pd.dOB = null
    pd.age = null
    pd.ageUnit = null
    pd.gender = null
    pd.country = null
    pd.county = null

    return pd
  }

  transformDriverLicenseInfo(
    pd: IPatientDemographicsState,
    pdo: IPatientDemographicsOptionsState,
    dlScanner: IDriversLicenseScannerState,
    dispatchedDate?: string | null
  ): void {
    const { licenseKeys, licenseInfo } = dlScanner

    pd = this.resetPatientDemographicsState(pd)

    licenseKeys.forEach((key: string) => {
      const value = licenseInfo[key]
      const { description, content } = value

      switch (key) {
        case 'DAC':
          pd.firstName = content
          break
        case 'DAD':
          if (content === 'NONE') {
            pd.middleInitial = ''
          } else {
            pd.middleInitial = content.substring(0, 1)
          }
          break
        case 'DCS':
          pd.lastName = content
          break
        case 'DAG':
          pd.address1 = content
          break
        case 'DAI':
          pd.city = { name: content, nemsisCode: '' }
          break
        case 'DAJ':
          pd.state = getOption(content, pdo.stateOptions)
          pd.dlState = getOption(content, pdo.stateOptions)
          break
        case 'DAK':
          pd.zipCode = convertZIPString(content)
          break
        case 'DAQ':
          pd.driversLicense = content
          break
        case 'DAU':
          pd.height = convertHeightString(content)
          pd.heightUnits = getOption(convertHeightUnitString(content), pdo.heightUnitOptions)
          break
        case 'DAW':
          pd.weight = convertWeightString(content)
          pd.weightUnits = getOption(convertWeightUnitString(description), pdo.weightUnitOptions)
          break
        case 'DBB':
          pd.dOB = transformDriversLicenseDate(content)
          pd.age = getAgeFromReference(pd.dOB, dispatchedDate ?? null)
          pd.ageUnit = getOption(getAgeUnitFromReference(pd.dOB, dispatchedDate ?? null), pdo.ageUnitOptions)
          break
        case 'DBC':
          pd.gender = getOption(convertGenderString(content), pdo.genderOptions)
          break
        case 'DCG':
          pd.country = getOption(convertCountryString(content), pdo.countryOptions)
          break
        case 'DBK':
          pd.sSN = content
          break
        case 'DBM':
          pd.sSN = content
          break
      }
    })
  }

  onFrameRead(results: any): void {
    for (const result of results) {
      const { barcodeText, exception } = result
      if (barcodeText.indexOf('Attention(exceptionCode') !== -1) {
        const { message } = exception
        Logger.error(`DriversLicenseScanner error in onFrameRead: ${message}`)
      }
    }
  }

  onUnduplicatedRead(dispatch: (action: () => void) => void, cb: () => void, txt: string): void {
    if (process.env.REACT_APP_USE_MOCK_DATA) {
      dispatch(setLicenseInfo(mockLicenseInfo))
      dispatch(setDriversLicenseKeys(Object.keys(mockLicenseInfo)))
    } else {
      if (/^\[Attention\(exceptionCode:/.test(txt)) {
        Logger.error(`DriversLicenseScanner error in onUnduplicatedRead: ${txt}`)
        return
      }

      Logger.info('DriversLicenseScanner successfully completed a scan of a license.')
      this.parseDriverLicense(txt)
      dispatch(setLicenseInfo(this.licenseInformation))
      dispatch(setDriversLicenseKeys(Object.keys(this.licenseInformation)))
    }

    cb()
  }

  isToggleKey(licenseKey: string): boolean {
    return ToggleKeyConstants.includes(licenseKey)
  }

  getLicenseKeys(licenseKey: string, keys: string[]): string[] {
    let _licenseKeys = []
    if (!keys) {
      _licenseKeys.push(licenseKey)
    } else {
      const index = keys.indexOf(licenseKey)
      if (index !== -1) {
        keys.splice(index, 1)
      } else {
        keys.push(licenseKey)
      }
      _licenseKeys = keys
    }
    return _licenseKeys
  }

  getToggleAllLicenseKeys(isToggleAll: boolean, licenseInfo: any): string[] {
    return isToggleAll ? Object.keys(licenseInfo) : []
  }
}
